import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Shield, CheckCircle2, AlertCircle, Lock } from 'lucide-react';
import { Layout } from '../components/Layout';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { TrustBadges } from '../components/ui/TrustBadges';
import { getAgreementByCode, payAgreement, getProviderProfile } from '../lib/persistence';
import { createPreference } from '../lib/mercadopago';
import { Agreement, CATEGORY_CONFIG, OUTCOME_LABELS } from '../types';
export function ClientView() {
  const {
    code
  } = useParams<{
    code: string;
  }>();
  const navigate = useNavigate();
  const [agreement, setAgreement] = useState<Agreement | null>(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  useEffect(() => {
    if (code) {
      getAgreementByCode(code).then(data => {
        setAgreement(data);
        setLoading(false);
      });
    }
  }, [code]);
  if (loading) {
    return <Layout data-id="element-211">
        <div className="flex items-center justify-center min-h-[60vh]" data-id="element-212">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" data-id="element-213" />
        </div>
      </Layout>;
  }
  if (!agreement) {
    return <Layout data-id="element-214">
        <div className="max-w-md mx-auto px-4 py-16 text-center" data-id="element-215">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" data-id="element-216" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-217">
            Acuerdo no encontrado
          </h1>
          <p className="text-gray-500" data-id="element-218">Verificá que el enlace sea correcto.</p>
        </div>
      </Layout>;
  }
  const categoryConfig = CATEGORY_CONFIG[agreement.category];
  const outcomeConfig = OUTCOME_LABELS[agreement.outcome_type];
  const formatCurrency = (amount: number) => new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: agreement.currency
  }).format(amount);
  const handlePay = async () => {
    setPaying(true);
    try {
      // BACKEND REQUIRED: En producción, llamamos a createPreference en el backend
      // y redirigimos al init_point de Mercado Pago.
      const providerProfile = getProviderProfile();
      const mpUserId = providerProfile.mp_connected ? providerProfile.mp_user_id! : 'demo_collector_id';
      const preference = await createPreference(agreement, mpUserId);
      // Simulamos que el usuario fue a MP, pagó, y volvió
      await payAgreement(agreement.agreement_code);
      // Redirigimos a la URL simulada (o real en prod)
      window.location.href = preference.init_point;
    } catch (error) {
      console.error(error);
      setPaying(false);
    }
  };
  return <Layout data-id="element-219">
      <div className="max-w-3xl mx-auto px-4 py-12" data-id="element-220">
        <div className="text-center mb-10" data-id="element-221">
          <div className="inline-flex items-center gap-2 bg-blue-50 text-blue-700 px-4 py-1.5 rounded-full text-sm font-medium mb-6" data-id="element-222">
            <Shield className="w-4 h-4" data-id="element-223" />
            Acuerdo protegido por Agreexa
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4" data-id="element-224">
            Revisá y asegurá tu acuerdo
          </h1>
          <p className="text-gray-500 max-w-xl mx-auto" data-id="element-225">
            {agreement.provider_name} te envió este acuerdo. Revisá los detalles
            y realizá el pago. Los fondos quedan protegidos hasta que confirmes
            que recibiste el servicio.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-226">
          <div className="md:col-span-2 space-y-6" data-id="element-227">
            <Card padding="lg" data-id="element-228">
              <div className="flex items-center gap-3 mb-6 pb-6 border-b border-gray-100" data-id="element-229">
                <div className="w-12 h-12 bg-gray-50 rounded-2xl flex items-center justify-center text-2xl" data-id="element-230">
                  {categoryConfig?.icon || '✨'}
                </div>
                <div data-id="element-231">
                  <h2 className="text-xl font-bold text-gray-900" data-id="element-232">
                    {agreement.title}
                  </h2>
                  <p className="text-sm text-gray-500" data-id="element-233">
                    Prestador: {agreement.provider_name}
                  </p>
                </div>
              </div>

              <div className="space-y-6" data-id="element-234">
                {agreement.description && <div data-id="element-235">
                    <h3 className="text-sm font-medium text-gray-900 mb-2" data-id="element-236">
                      Descripción del servicio
                    </h3>
                    <p className="text-sm text-gray-600 whitespace-pre-wrap bg-gray-50 p-4 rounded-xl" data-id="element-237">
                      {agreement.description}
                    </p>
                  </div>}

                <div className="grid grid-cols-2 gap-4" data-id="element-238">
                  {agreement.service_date && <div data-id="element-239">
                      <h3 className="text-sm font-medium text-gray-900 mb-1" data-id="element-240">
                        Fecha
                      </h3>
                      <p className="text-sm text-gray-600" data-id="element-241">
                        {new Date(agreement.service_date).toLocaleDateString('es-AR')}
                        {agreement.service_date_end && ` al ${new Date(agreement.service_date_end).toLocaleDateString('es-AR')}`}
                      </p>
                    </div>}
                  {agreement.service_location && <div data-id="element-242">
                      <h3 className="text-sm font-medium text-gray-900 mb-1" data-id="element-243">
                        Lugar / Modalidad
                      </h3>
                      <p className="text-sm text-gray-600" data-id="element-244">
                        {agreement.service_location}
                      </p>
                    </div>}
                </div>

                <div data-id="element-245">
                  <h3 className="text-sm font-medium text-gray-900 mb-2" data-id="element-246">
                    ¿Cómo se confirma el servicio?
                  </h3>
                  <div className="flex items-start gap-3 bg-blue-50/50 p-4 rounded-xl border border-blue-100" data-id="element-247">
                    <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" data-id="element-248" />
                    <div data-id="element-249">
                      <p className="text-sm font-medium text-gray-900" data-id="element-250">
                        {outcomeConfig?.label}
                      </p>
                      <p className="text-xs text-gray-600 mt-1" data-id="element-251">
                        {outcomeConfig?.description}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </div>

          <div className="md:col-span-1" data-id="element-252">
            <div className="sticky top-24" data-id="element-253">
              <Card className="mb-6" data-id="element-254">
                <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-255">
                  Resumen de pago
                </h3>

                <div className="bg-blue-50/50 border border-blue-100 rounded-xl p-4 mb-6" data-id="element-256">
                  <p className="text-xs text-blue-800 font-medium mb-1" data-id="element-257">
                    Modelo de pago directo
                  </p>
                  <p className="text-xs text-blue-600 leading-relaxed" data-id="element-258">
                    El pago va directo a la cuenta de Mercado Pago de{' '}
                    <strong data-id="element-259">{agreement.provider_name}</strong>. Agreexa asegura
                    que los fondos no se liberen hasta que confirmes el
                    servicio.
                  </p>
                </div>

                <div className="space-y-3 mb-6" data-id="element-260">
                  <div className="flex justify-between text-sm" data-id="element-261">
                    <span className="text-gray-500" data-id="element-262">Servicio</span>
                    <span className="font-medium text-gray-900" data-id="element-263">
                      {formatCurrency(agreement.amount)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm" data-id="element-264">
                    <span className="text-gray-500" data-id="element-265">Protección Agreexa</span>
                    <span className="text-green-600 font-medium" data-id="element-266">
                      Bonificada
                    </span>
                  </div>
                  <div className="pt-3 border-t border-gray-100 flex justify-between items-center" data-id="element-267">
                    <span className="font-bold text-gray-900" data-id="element-268">
                      Total a pagar
                    </span>
                    <span className="text-2xl font-black text-gray-900" data-id="element-269">
                      {formatCurrency(agreement.amount)}
                    </span>
                  </div>
                </div>

                {agreement.status === 'pending_payment' ? <Button fullWidth size="lg" onClick={handlePay} loading={paying} icon={<Lock className="w-4 h-4" data-id="element-271" />} data-id="element-270">
                    Pagar y asegurar fondos
                  </Button> : <div className="bg-green-50 text-green-700 p-4 rounded-xl text-center text-sm font-medium flex items-center justify-center gap-2" data-id="element-272">
                    <CheckCircle2 className="w-5 h-5" data-id="element-273" />
                    Acuerdo ya pagado
                  </div>}

                <p className="text-xs text-gray-400 text-center mt-4" data-id="element-274">
                  El prestador no recibe el dinero hasta que confirmes que el
                  servicio fue prestado.
                </p>
              </Card>

              <TrustBadges size="sm" data-id="element-275" />
            </div>
          </div>
        </div>
      </div>
    </Layout>;
}